import { BellRing } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useDeviceDetailAccess } from '@/components/monitoring/device_detail/useDeviceDetailAccess';
import { cx, focusRingClass, interactiveClass } from '@/components/ui/shared';
import { consoleRoutePaths } from '@/navigation/consoleRoutePaths';
import { stageSummaryCounts } from '@/pages/admin/device_accounts/deviceInventoryPrimaryAction';

import { useConsoleFleet } from './useConsoleFleet';

const deviceCount = (count: number) => `${count} ${count === 1 ? 'device' : 'devices'}`;

/**
 * "Needs you: N" in the shell: devices waiting for approval, blocked setup or
 * paused, counted exactly like the Devices page "Needs you" tile. Shown only
 * to people who can act on a device, and only when something is waiting.
 */
export default function ConsoleNeedsYouIndicator() {
  const { canControlDevice } = useDeviceDetailAccess();
  const fleetQuery = useConsoleFleet(canControlDevice);
  const stages = fleetQuery.data?.counts.stages;
  if (!canControlDevice || !stages) return null;

  const count = stageSummaryCounts(stages).needs_you;
  if (count === 0) return null;

  return (
    <Link
      to={consoleRoutePaths.devices}
      title={`${deviceCount(count)} ${count === 1 ? 'needs' : 'need'} you. Open Devices to see which.`}
      className={cx(
        focusRingClass,
        interactiveClass,
        'inline-flex min-h-control-comfortable shrink-0 items-center gap-2 rounded-control border border-status-failed-border bg-status-failed-surface px-3 text-control font-semibold text-status-failed',
      )}
    >
      <BellRing className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only sm:not-sr-only">Needs you:</span>
      <span className="so-data-text">{count}</span>
      <span className="sr-only">{count === 1 ? 'device' : 'devices'}</span>
    </Link>
  );
}
